import { Route, Routes } from "react-router";
import * as ROUTES from "../constants/routes";
import type { useAppInitialization } from "../hooks/useAppInitialization";
import { JwtAction } from "../pages";
import { CollabageddonCheckout } from "../pages/collabageddon/CollabageddonCheckout";
import { DiscoveryV2PreOrders } from "../pages/discovery/v2/DiscoverV2PreOrders";
import { DiscoveryV2BestSellersList } from "../pages/discovery/v2/DiscoveryV2BestSellersList";
import { DiscoveryV2DistributionLandingPage } from "../pages/discovery/v2/distribution/DiscoveryV2DistributionLandingPage";
import { DiscoveryV2Home } from "../pages/discovery/v2/DiscoveryV2Home";
import { LowAndNo } from "../pages/discovery/v2/landingPages/LowAndNo";
import { DiscoveryV2RecentlyAdded } from "../pages/discovery/v2/DiscoveryV2RecentlyAdded";
import { DiscoveryV2SupplierMap } from "../pages/discovery/v2/DiscoveryV2SupplierMap";
import { DiscoveryV2SuppliersDirectList } from "../pages/discovery/v2/DiscoveryV2SuppliersDirectList";
import { DiscoveryV2SuppliersRecentList } from "../pages/discovery/v2/DiscoveryV2SuppliersRecentList";
import { LondonCraftBeerFestival } from "../pages/londonCraftBeerFestival/LondonCraftBeerFestival";
import { ProductSpotlightOverviewPage } from "../pages/spotlight/OverviewPages/ProductSpotlightOverviewPage";
import { RegionalSpotlightOverviewPage } from "../pages/spotlight/OverviewPages/RegionalSpotlightOverviewPage";
import { Spotlight } from "../pages/spotlight/Spotlight";
import { Collabageddon } from "~/pages";
import { AppFallbackRedirect } from "./AppFallbackRedirect";
import { getAppRouteCapabilities } from "./getAppRouteCapabilities";
import { renderAuthenticatedBusinessRoutes } from "./renderAuthenticatedBusinessRoutes";
import { renderAuthenticationRoutes } from "./renderAuthenticationRoutes";
import { renderExternalFlowRoutes } from "./renderExternalFlowRoutes";
import { renderRetailerRoutes } from "./renderRetailerRoutes";
import { renderStorefrontRoutes } from "./renderStorefrontRoutes";
import { renderSupplierOnboardingRoutes } from "./renderSupplierOnboardingRoutes";
import { resolveAuthenticatedAccessMode } from "./resolveAuthenticatedAccessMode";
import { DiscoverySearchRedirect } from "~/pages/discovery/DiscoverySearchRedirect";

type AppRoutesProps = {
  initialization: ReturnType<typeof useAppInitialization>;
};

export const AppRoutes = ({ initialization }: AppRoutesProps) => {
  const {
    firebase: { user },
    sellar: { user: sellarUser },
    currentBusiness: { business },
    remoteFeatureFlags,
    signup: { redirectToMarket, redirectToSellarCreditOnboarding },
  } = initialization;
  const accessMode = resolveAuthenticatedAccessMode({ business, sellarUser });

  const {
    invoiceSettingsEnabled,
    inAppNotificationsActive,
    unbundlingRelease2bEnabled,
    unbundlingRelease2aEnabled,
    unbundlingEnabled,
    supplierOrderPromotionsEnabled,
    sellarDistributionBetaEnabled,
    canManageCompanyCreditSettings,
    canAccessCreditTab,
  } = getAppRouteCapabilities({
    business,
    sellarUser,
    isFeatureActive: remoteFeatureFlags.isActive,
  });

  const renderFallbackRedirect = () => (
    <AppFallbackRedirect
      hasFirebaseUser={Boolean(user)}
      sellarUser={sellarUser}
      business={business}
      redirectToMarket={redirectToMarket}
      redirectToSellarCreditOnboarding={redirectToSellarCreditOnboarding}
    />
  );

  return (
    <Routes>
      <Route path={ROUTES.JWT} element={<JwtAction />} />

      {renderExternalFlowRoutes()}
      {renderStorefrontRoutes()}
      {renderRetailerRoutes({ initialization })}
      {renderSupplierOnboardingRoutes({
        accessMode,
        renderFallback: renderFallbackRedirect,
      })}
      {renderAuthenticatedBusinessRoutes({
        firebaseUser: user,
        sellarUser,
        business,
        invoiceSettingsEnabled,
        inAppNotificationsActive,
        unbundlingRelease2bEnabled,
        unbundlingRelease2aEnabled,
        unbundlingEnabled,
        supplierOrderPromotionsEnabled,
        sellarDistributionBetaEnabled,
        canManageCompanyCreditSettings,
        canAccessCreditTab,
        canAccessOperationalRoutes: accessMode === "operational",
        renderFallbackRedirect,
      })}
      {renderAuthenticationRoutes({
        firebaseUser: user,
        sellarUser,
        business,
      })}

      {(business?.Retailer?.id || !user || !business) && (
        <>
          <Route path={ROUTES.DISCOVER} element={<DiscoveryV2Home />} />
          <Route
            path={ROUTES.DISCOVER_SEARCH}
            element={<DiscoverySearchRedirect />}
          />
          <Route
            path={ROUTES.DISCOVER_SUPPLIER_MAP}
            element={<DiscoveryV2SupplierMap />}
          />
          <Route
            path={ROUTES.DISCOVER_RECENTLY_ADDED}
            element={<DiscoveryV2RecentlyAdded />}
          />
          <Route
            path={ROUTES.DISCOVER_BEST_SELLERS}
            element={<DiscoveryV2BestSellersList />}
          />
          <Route
            path={ROUTES.DISCOVER_SUPPLIERS_DIRECT}
            element={<DiscoveryV2SuppliersDirectList />}
          />
          <Route
            path={ROUTES.DISCOVER_SUPPLIERS_RECENT}
            element={<DiscoveryV2SuppliersRecentList />}
          />
          <Route
            path={ROUTES.DISCOVER_REGIONAL_SPOTLIGHT}
            element={<RegionalSpotlightOverviewPage />}
          />
          <Route
            path={ROUTES.DISCOVER_PRODUCT_SPOTLIGHT}
            element={<ProductSpotlightOverviewPage />}
          />
          <Route
            path={ROUTES.DISCOVER_PRE_ORDER}
            element={<DiscoveryV2PreOrders />}
          />
          <Route
            path={ROUTES.DISCOVER_DISTRIBUTION}
            element={<DiscoveryV2DistributionLandingPage />}
          />
          <Route path={ROUTES.LCBF} element={<LondonCraftBeerFestival />} />
          <Route path={ROUTES.LOW_AND_NO} element={<LowAndNo />} />
          <Route path={ROUTES.SPOTLIGHT.pattern} element={<Spotlight />} />
          <Route path={ROUTES.COLLABAGEDDON} element={<Collabageddon />} />
          <Route
            path={ROUTES.COLLABAGEDDON_CHECKOUT}
            element={<CollabageddonCheckout />}
          />
        </>
      )}
      <Route path="*" element={renderFallbackRedirect()} />
    </Routes>
  );
};
